import React, { useRef } from 'react';
import { useLanguage } from './i18n/useLanguage';
import { getPrivacyText } from './i18n/privacy/getPrivacy';
import { legalCopy } from './i18n/legal';
import PageMetadata from './PageMetadata';
import SectionLabel from './SectionLabel';
import { SiteHeader, SiteFooter, IconSprite, Arrow } from './SiteChrome';
import './LegalPages.css';

function PolicyText({ children }) {
  return <>{children.split(/(support@plannely\.app)/g).map((text, index) => text === 'support@plannely.app' ? <a key={index} href="mailto:support@plannely.app">{text}</a> : text)}</>;
}

export default function PrivacyPolicy() {
  const [language, changeLanguage] = useLanguage();
  const contents = useRef(null);
  const p = key => getPrivacyText(key, language);
  const copy = legalCopy(language);
  const toc = () => <nav className="legal-toc" aria-label={copy.contents}>{Array.from({ length: 14 }, (_, index) => <a key={index} href={'#privacy-section-' + (index + 1)} onClick={() => { if (contents.current) contents.current.open = false; }}>{p('privacy.section' + (index + 1) + '.title')}</a>)}</nav>;
  return <div className="plannely-layout">
    <PageMetadata language={language} title={p('privacy.meta.title')} description={p('privacy.meta.description')} url="https://plannely.app/privacy-policy" />
    <IconSprite />
    <div className="wrap" id="top">
      <SiteHeader language={language} onLanguageChange={changeLanguage} />
      <main className="legal-main">
        <header className="legal-intro">
          <SectionLabel>Plannely · SEOTIV</SectionLabel>
          <h1>{p('privacy.header.title')}</h1>
          <p>{p('privacy.meta.description')}</p>
          <div className="legal-meta">
            <p>{p('privacy.header.effectiveDate')} <strong>{p('privacy.header.effectiveDateValue')}</strong></p>
            <p>{p('privacy.header.appNameLabel')} <strong>{p('privacy.header.appNameValue')}</strong></p>
          </div>
        </header>
        <details className="legal-toc-mobile" ref={contents}><summary>{copy.contents}<Arrow /></summary>{toc()}</details>
        <div className="legal-body">
          <aside className="legal-sidebar"><h2>{copy.contents}</h2>{toc()}</aside>
          <article className="legal-document">
          {/* 1. Кто мы / Who we are */}
          <section className="legal-section" id="privacy-section-1">
            <h2>{p('privacy.section1.title')}</h2>
            <p><PolicyText>{p('privacy.section1.p1')}</PolicyText></p>
            <p><PolicyText>{p('privacy.section1.p2')}</PolicyText></p>
          </section>

          {/* 2. Какие данные мы собираем */}
          <section className="legal-section" id="privacy-section-2">
            <h2>{p('privacy.section2.title')}</h2>

            <h3>{p('privacy.section2.account.title')}</h3>
            <p><PolicyText>{p('privacy.section2.account.p1')}</PolicyText></p>
            <ul>
              <li>{p('privacy.section2.account.li1')}</li>
              <li>{p('privacy.section2.account.li2')}</li>
              <li>{p('privacy.section2.account.li3')}</li>
            </ul>
            <p><PolicyText>{p('privacy.section2.account.p2')}</PolicyText></p>

            <h3>{p('privacy.section2.tasks.title')}</h3>
            <p><PolicyText>{p('privacy.section2.tasks.p1')}</PolicyText></p>
            <ul>
              <li>{p('privacy.section2.tasks.li1')}</li>
              <li>{p('privacy.section2.tasks.li2')}</li>
              <li>{p('privacy.section2.tasks.li3')}</li>
            </ul>
            <p><PolicyText>{p('privacy.section2.tasks.p2')}</PolicyText></p>

            <h3>{p('privacy.section2.tech.title')}</h3>
            <p><PolicyText>{p('privacy.section2.tech.p1')}</PolicyText></p>
            <ul>
              <li>{p('privacy.section2.tech.li1')}</li>
              <li>{p('privacy.section2.tech.li2')}</li>
              <li>{p('privacy.section2.tech.li3')}</li>
              <li>{p('privacy.section2.tech.li4')}</li>
            </ul>

            <h3>{p('privacy.section2.notifications.title')}</h3>
            <p><PolicyText>{p('privacy.section2.notifications.p1')}</PolicyText></p>
            <ul>
              <li>{p('privacy.section2.notifications.li1')}</li>
              <li>{p('privacy.section2.notifications.li2')}</li>
            </ul>
          </section>

          {/* 3. Как мы используем данные */}
          <section className="legal-section" id="privacy-section-3">
            <h2>{p('privacy.section3.title')}</h2>
            <p><PolicyText>{p('privacy.section3.p1')}</PolicyText></p>
            <ul>
              <li>{p('privacy.section3.li1')}</li>
              <li>{p('privacy.section3.li2')}</li>
              <li>{p('privacy.section3.li3')}</li>
              <li>{p('privacy.section3.li4')}</li>
              <li>{p('privacy.section3.li5')}</li>
              <li>{p('privacy.section3.li6')}</li>
            </ul>
            <p><PolicyText>{p('privacy.section3.p2')}</PolicyText></p>
          </section>

          {/* 4. Правовые основания */}
          <section className="legal-section" id="privacy-section-4">
            <h2>{p('privacy.section4.title')}</h2>
            <p><PolicyText>{p('privacy.section4.p1')}</PolicyText></p>
            <ul>
              <li>{p('privacy.section4.li1')}</li>
              <li>{p('privacy.section4.li2')}</li>
              <li>{p('privacy.section4.li3')}</li>
            </ul>
            <p><PolicyText>{p('privacy.section4.p2')}</PolicyText></p>
          </section>

          {/* 5. Cookie */}
          <section className="legal-section" id="privacy-section-5">
            <h2>{p('privacy.section5.title')}</h2>
            <p><PolicyText>{p('privacy.section5.p1')}</PolicyText></p>
            <ul>
              <li>{p('privacy.section5.li1')}</li>
              <li>{p('privacy.section5.li2')}</li>
              <li>{p('privacy.section5.li3')}</li>
            </ul>
            <p><PolicyText>{p('privacy.section5.p2')}</PolicyText></p>
          </section>

          {/* 6. Хранение и бэкапы */}
          <section className="legal-section" id="privacy-section-6">
            <h2>{p('privacy.section6.title')}</h2>
            <p><PolicyText>{p('privacy.section6.p1')}</PolicyText></p>
            <p><PolicyText>{p('privacy.section6.p2')}</PolicyText></p>
            <h3>{p('privacy.section6.backups.title')}</h3>
            <p><PolicyText>{p('privacy.section6.backups.p1')}</PolicyText></p>
            <p><PolicyText>{p('privacy.section6.backups.p2')}</PolicyText></p>
          </section>

          {/* 7. Передача данных */}
          <section className="legal-section" id="privacy-section-7">
            <h2>{p('privacy.section7.title')}</h2>
            <p><PolicyText>{p('privacy.section7.p1')}</PolicyText></p>
            <ul>
              <li>{p('privacy.section7.li1')}</li>
              <li>{p('privacy.section7.li2')}</li>
              <li>{p('privacy.section7.li3')}</li>
              <li>{p('privacy.section7.li4')}</li>
            </ul>
            <p><PolicyText>{p('privacy.section7.p2')}</PolicyText></p>
            <p><PolicyText>{p('privacy.section7.p3')}</PolicyText></p>
            <p><PolicyText>{p('privacy.section7.p4')}</PolicyText></p>
            <p><PolicyText>{p('privacy.section7.p5')}</PolicyText></p>
          </section>

          {/* 8. Международная передача */}
          <section className="legal-section" id="privacy-section-8">
            <h2>{p('privacy.section8.title')}</h2>
            <p><PolicyText>{p('privacy.section8.p1')}</PolicyText></p>
            <p><PolicyText>{p('privacy.section8.p2')}</PolicyText></p>
            <p><PolicyText>{p('privacy.section8.p3')}</PolicyText></p>
          </section>

          {/* 9. Безопасность */}
          <section className="legal-section" id="privacy-section-9">
            <h2>{p('privacy.section9.title')}</h2>
            <p><PolicyText>{p('privacy.section9.p1')}</PolicyText></p>
            <ul>
              <li>{p('privacy.section9.li1')}</li>
              <li>{p('privacy.section9.li2')}</li>
              <li>{p('privacy.section9.li3')}</li>
              <li>{p('privacy.section9.li4')}</li>
            </ul>
            <p><PolicyText>{p('privacy.section9.p2')}</PolicyText></p>
          </section>

          {/* 10. Ваши права */}
          <section className="legal-section" id="privacy-section-10">
            <h2>{p('privacy.section10.title')}</h2>
            <p><PolicyText>{p('privacy.section10.p1')}</PolicyText></p>
            <ul>
              <li>{p('privacy.section10.li1')}</li>
              <li>{p('privacy.section10.li2')}</li>
              <li>{p('privacy.section10.li3')}</li>
              <li>{p('privacy.section10.li4')}</li>
              <li>{p('privacy.section10.li5')}</li>
              <li>{p('privacy.section10.li6')}</li>
              <li>{p('privacy.section10.li7')}</li>
              <li>{p('privacy.section10.li8')}</li>
            </ul>
            <p><PolicyText>{p('privacy.section10.p2')}</PolicyText></p>
          </section>

          {/* 11. Удаление аккаунта */}
          <section className="legal-section" id="privacy-section-11">
            <h2>{p('privacy.section11.title')}</h2>
            <p><PolicyText>{p('privacy.section11.p1')}</PolicyText></p>
            <ul>
              <li>{p('privacy.section11.li1')}</li>
              <li>{p('privacy.section11.li2')}</li>
            </ul>
            <p><PolicyText>{p('privacy.section11.p2')}</PolicyText></p>
            <p><PolicyText>{p('privacy.section11.p3')}</PolicyText></p>
          </section>

          {/* 12. Изменения политики */}
          <section className="legal-section" id="privacy-section-12">
            <h2>{p('privacy.section12.title')}</h2>
            <p><PolicyText>{p('privacy.section12.p1')}</PolicyText></p>
            <p><PolicyText>{p('privacy.section12.p2')}</PolicyText></p>
            <p><PolicyText>{p('privacy.section12.p3')}</PolicyText></p>
          </section>

          {/* 13. Контакты */}
          <section className="legal-section" id="privacy-section-13">
            <h2>{p('privacy.section13.title')}</h2>
            <p><PolicyText>{p('privacy.section13.p1')}</PolicyText></p>
            <p><PolicyText>{p('privacy.section13.p2')}</PolicyText></p>
          </section>

{/* 14. Children’s privacy */}
<section className="legal-section" id="privacy-section-14">
            <h2>{p('privacy.section14.title')}</h2>
  <p><PolicyText>{p('privacy.section14.p1')}</PolicyText></p>
  <p><PolicyText>{p('privacy.section14.p2')}</PolicyText></p>
  <p><PolicyText>{p('privacy.section14.p3')}</PolicyText></p>

</section>


          </article>
        </div>
        <a className="legal-back" href="/"><Arrow />{copy.back}</a>
      </main>
      <SiteFooter language={language} currentPage="privacy" />
    </div>
  </div>;
}
